import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import type { IAssetFileUploaded, ICreateOrUpdateFile, IFileUploaded, IUploadFile } from '~/types/file'

import { createAssetFile, createFile, getFile, uploadFile } from '~/api/file'

export const fileKeys = {
  all: ['files'] as const,
  batchDetails: (fileIds?: string[]) => [...fileKeys.all, 'batch-details', fileIds] as const,
  batchFiles: (fileIds?: string[]) => [...fileKeys.all, 'batch-classic', fileIds] as const,
  details: (fileId?: string) => [...fileKeys.all, 'details', fileId] as const,
}

////////// QUERIES //////////
export const useFileDetailsQuery = <T = IFileUploaded>(fileId?: string, enabled?: boolean): UseQueryResult<T> => {
  return useQuery({
    enabled: !!fileId && enabled,
    queryFn: () => getFile({ fileId: fileId as string }),
    queryKey: fileKeys.details(fileId as string),
  })
}

/** @whisper The getFile call returns more properties than batchGetFile; this query calls getFile for each fileId provided  */
export const useFilesDetailsQuery = (fileIds: string[]): UseQueryResult<IFileUploaded[]> => {
  const queryClient = useQueryClient()
  return useQuery({
    queryFn:
      fileIds?.length > 0
        ? async () => {
            return Promise.all(
              fileIds.map((fileId) =>
                queryClient.fetchQuery({
                  queryFn: () => getFile({ fileId }),
                  queryKey: fileKeys.details(fileId as string),
                })
              )
            )
          }
        : skipToken,
    queryKey: fileKeys.batchDetails(fileIds),
  })
}

//////// MUTATIONS //////////
export const useCreateFileMutation = (): UseMutationResult<IFileUploaded, unknown, ICreateOrUpdateFile> => {
  return useMutation({ mutationFn: createFile })
}

export const useCreateAssetFileMutation = (): UseMutationResult<IAssetFileUploaded, unknown, ICreateOrUpdateFile> => {
  return useMutation({ mutationFn: createAssetFile })
}

export const useUploadFileMutation = (): UseMutationResult<void, unknown, IUploadFile> => {
  return useMutation({ mutationFn: uploadFile })
}
